import { useMutation, useQuery } from 'react-query';
import { useEffect } from 'react';

import { getTaskLogs, getTaskNotes, getTicketNoteTypes, postCheckTaskLock, postTaskNote } from 'queries';
import { parseErrors, Toast } from 'helpers';
import { JsonDataItem } from 'types';

export const useTicketDetails = (taskId: number, isOpen: boolean, showLogs: boolean) => {
  // Details
  const {
    mutate,
    data: lockData,
    isLoading: checkingLockStatus,
  } = useMutation(postCheckTaskLock, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Notes
  const {
    data: notes,
    isLoading: isNotesLoading,
    refetch: refetchNotes,
  } = useQuery(['task-notes', taskId, isOpen], () => getTaskNotes({ status: Number(isOpen), taskId: taskId }), {
    enabled: !!taskId,
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { data: noteTypes } = useQuery(['note-types'], getTicketNoteTypes, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const {
    mutate: addNote,
    isLoading: isNoteAdding,
    isSuccess: isNoteAdded,
  } = useMutation(postTaskNote, {
    onSuccess: () => {
      refetchNotes();
      Toast.show({ description: 'A new note has been added.', type: Toast.Type.Success });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  //  Logs
  const { data: logs, isLoading: isLogsLoading } = useQuery(
    ['task-logs', taskId, isOpen],
    () => getTaskLogs({ status: Number(isOpen), taskId }),
    {
      enabled: !!taskId && !!showLogs,
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    },
  );

  useEffect(() => {
    taskId && isOpen && mutate({ taskId });
    refetchNotes();
  }, [taskId]);

  return {
    lockData,
    isDetailsLoading: checkingLockStatus || isNotesLoading || isLogsLoading || isNoteAdding,
    notes: notes?.data,
    logs: logs?.data,
    noteTypes,
    addNote,
    isNoteAdded,
  };
};
